import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { AddressValue, SearchEngineFieldAddress } from '@domains/search-engine/entities/address-search-field';
import { SearchEngineQueryParams } from '@domains/search-engine/search-engine-field';
import { PoleEmploiCity } from '@models/pole-emploi/city';
import { PoleEmploiDepartment } from '@models/pole-emploi/department';
import { PoleEmploiRegion } from '@models/pole-emploi/region';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { City } from '@wizbii/models';
import { CityWebservice } from '@wizbii/webservices';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-address-search',
  templateUrl: './address-search.component.html',
  styleUrls: ['./address-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressSearchComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressSearchComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() field!: SearchEngineFieldAddress;
  @Input() modalOpen!: boolean;
  @Input() liveSearch!: boolean;

  @Output() hasChanged = new EventEmitter<boolean>();

  private _value?: AddressValue;

  locationIsFocused = false;

  cities$ = new Observable<City[]>();
  error$ = new BehaviorSubject<boolean>(false);

  address = new FormControl(undefined);

  address$ = new BehaviorSubject<string>('');

  set value(val: AddressValue | undefined) {
    if (this._value === val) {
      return;
    }
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): AddressValue | undefined {
    return this._value;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly cityWebservice: CityWebservice,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        tap((params) => this.address$.next(params.get(SearchEngineQueryParams.Address) || '')),
        map((params) => params.get(SearchEngineQueryParams.CityPlaceId)),
        distinctUntilChanged(),
        switchMap((cityPlaceId) => (cityPlaceId ? this.cityWebservice.get(cityPlaceId) : of(null))),
        shareReplay({ bufferSize: 1, refCount: true }),
        take(1)
      )
      .subscribe({
        next: (city: any) => {
          this.address.setValue(city ? { ...city, address: this.address$.value } : undefined);

          this.cdr.detectChanges();
        },
      });

    this.address.valueChanges.pipe(debounceTime(300), untilDestroyed(this)).subscribe({
      next: (val) => {
        if (typeof val === 'object') {
          this.value = { id: val.cityPlaceId, address: val.address };
        }
        if (val === '') {
          this.value = undefined;
        }
      },
    });

    this.cities$ = this.address.valueChanges.pipe(
      debounceTime(300),
      untilDestroyed(this),
      filter((query) => query && query.length > 0),
      distinctUntilChanged(),
      switchMap((query) => this.cityWebservice.getBy({ query, countries: ['FR'], type: 'address' })),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cities$
      .pipe(
        untilDestroyed(this),
        map((cities) => cities?.length === 0)
      )
      .subscribe({
        next: (val) => {
          this.error$.next(val);
        },
      });
  }

  onClose(): void {
    if (this.value || this.address.value === '') {
      return;
    }

    this.cities$
      .pipe(
        take(1),
        map((cities) => cities[0]),
        filter((city) => !!city)
      )
      .subscribe({
        next: (city) => {
          this.address.setValue(city);
        },
      });
  }

  selectValue(city: City): void {
    this.value = { address: city.address, id: city.cityPlaceId };

    if (this.liveSearch) {
      this.hasChanged.emit();
    }
  }

  clearValue(): void {
    this.address.setValue('');
    this.error$.next(false);

    this.value = undefined;

    if (this.liveSearch) {
      this.hasChanged.emit();
    }
  }

  trackByString(_: number, str: string): string {
    return str;
  }

  displayFn(location?: City): string {
    if (!location) {
      return '';
    }
    return location.address
      ? `${location.address} - ${location.city}, ${location.department}`
      : `${location.city}, ${location.department}`;
  }

  onChange: (val?: AddressValue) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val?: AddressValue): void {
    if (val?.id || val?.address) {
      this.value = val;
    } else {
      this.address.setValue('');
      this.value = undefined;
    }
  }

  registerOnChange(fn: (val?: AddressValue) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  trackByPoleEmploiCity(_: number, city: PoleEmploiCity): string {
    return city.code;
  }

  trackByPoleEmploiDepartment(_: number, department: PoleEmploiDepartment): string {
    return department.libelle;
  }

  trackByPoleEmploiRegion(_: number, region: PoleEmploiRegion): string {
    return region.code;
  }

  trackByCity(_: number, city: City): string {
    return city.city;
  }

  ngOnDestroy(): void {
    this.error$.complete();
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    NgUpperFirstPipeModule,
  ],
  declarations: [AddressSearchComponent],
  exports: [AddressSearchComponent],
})
export class SearchEngineAddressSearchModule {}
